/**
 * @license
 * Copyright 厦门乾元盛世科技有限公司 All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file.
 */


import { Component, Input, ElementRef, Renderer, ViewEncapsulation, HostBinding } from '@angular/core';
import { ContentChild } from '@angular/core';
import classNames from 'classnames';

import { TableHeader } from './table.header';
import { TableFooter } from './table.footer';


const /** @type {?} */ defaultLocale = {
    filterTitle: '筛选',
    filterConfirm: '确定',
    filterReset: '重置',
    emptyText: '暂无数据',
    selectAll: '全选当页',
    selectInvert: '反选当页',
};
/**
 * 表格
 */
export class Table {
/**
 * 样式前缀
 */
prefixCls = 'ant-table';
/**
 * 大小类型：正常型、紧凑型或迷你型，`default`, `middle`, `small`，默认为`default`
 */
size: 'default' | 'middle' | 'small' = 'default';
/**
 * 表格标题
 */
title: string;
/**
 * 表格脚注
 */
footer: string;
/**
 * 固定列头
 */
fixedHeader = false;
/**
 * 是否展示外边框和列边框
 */
bordered = false;
/**
 * 是否显示表头
 */
showHeader = true;
/**
 * 页面是否加载中
 */
loading = false;
/**
 * 是否没有数据
 */
isEmpty = false;
/**
 * 展示树形数据时，每层缩进的宽度，以 px 为单位
 */
indentSize = 20;
/**
 * 用户CSS样式
 */
class: string;
/**
 * 国际化
 */
locale: { [key: string]: string };
/**
 * 固定表头或者固定列的设置，例如 { y: 240 } 表示内容部分的高度为240px，超出范围的内容需要滚动查看
 */
scroll: {x?: number, y?: number};

     scrollPosition: 'left'|'right'|'both'|'middle' = 'left';
/**
 * 当固定列头时，可指定内容部分的最大高度
 */
bodyMaxHeight: number;
/**
 * 内容部分的overflow样式
 */
overflowY: 'auto' | 'scroll' | 'hidden' | 'visible' | 'overlay' = 'auto';
/**
 * 用户自定表格标题
 */
tableHeader: TableHeader;
/**
 * 用户自定表格脚注
 */
tableFooter: TableFooter;
/**
 * @param {?} _renderer
 * @param {?} _elementRef
 */
constructor(private _renderer: Renderer,
private _elementRef: ElementRef) {

    }
/**
 * @return {?}
 */
get classes(): string {
        return classNames({
            [`${this.prefixCls}-wrapper`]: 1
        }, this.class);
    }
/**
 * @return {?}
 */
getTableClasses(): any {
        return {
            [`${this.prefixCls}`]: true,
            [`${this.prefixCls}-${this.size}`]: true,
            [`${this.prefixCls}-bordered`]: this.bordered,
            [`${this.prefixCls}-empty`]: this.isEmpty,
            [`${this.prefixCls}-without-column-header`]: !this.showHeader,
            [`${this.prefixCls}-fixed-header`]: this.fixedHeader,
            [`${this.prefixCls}-scroll-position-left`]: this.scrollPosition === 'left' || this.scrollPosition === 'both',
            [`${this.prefixCls}-scroll-position-right`]: this.scrollPosition === 'right' || this.scrollPosition === 'both',
            [`${this.prefixCls}-scroll-position-middle`]: this.scrollPosition === 'middle'
        };
    }
/**
 * @return {?}
 */
getLocale() {
        return Object.assign({}, defaultLocale, this.locale);
    }
/**
 * 表格内容是否可滚动（表格列头将固定，或者左右列将固定）
 * @return {?}
 */
isScrollable(): boolean {
        const /** @type {?} */ scroll = this.scroll || {};
        return /*isAnyColumnsFixed()||*/ (!!scroll.x) || (!!scroll.y);
    }
/**
 * @return {?}
 */
isHeaderFixed(): boolean {
        const /** @type {?} */ scroll = this.scroll || {};
        return (!!scroll.y) || this.fixedHeader;
    }
/**
 * @return {?}
 */
getBodyStyle(): any {
        if (this.isHeaderFixed()) {
            const /** @type {?} */ scroll = this.scroll || {};
            return {
                maxHeight: this.bodyMaxHeight || scroll.y,
                overflowY: this.overflowY || 'scroll'
            };
        }
        return null;
    }
/**
 * @return {?}
 */
getContentTableClass(): any {
        const /** @type {?} */ scroll = this.scroll || {};
        return {
            [`${this.prefixCls}-fixed`]: (!!scroll.x)
        };
    }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'ant-table',
    templateUrl: './table.html',
    styleUrls: ['./style/index.scss'],
    encapsulation: ViewEncapsulation.None
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer, },
{type: ElementRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'prefixCls': [{ type: Input },],
'size': [{ type: Input },],
'title': [{ type: Input },],
'footer': [{ type: Input },],
'fixedHeader': [{ type: Input },],
'bordered': [{ type: Input },],
'showHeader': [{ type: Input },],
'loading': [{ type: Input },],
'isEmpty': [{ type: Input },],
'indentSize': [{ type: Input },],
'class': [{ type: Input },],
'locale': [{ type: Input },],
'scroll': [{ type: Input },],
'scrollPosition': [{ type: Input },],
'bodyMaxHeight': [{ type: Input },],
'overflowY': [{ type: Input },],
'tableHeader': [{ type: ContentChild, args: [TableHeader, ] },],
'tableFooter': [{ type: ContentChild, args: [TableFooter, ] },],
'classes': [{ type: HostBinding, args: ['class', ] },],
};
}

function Table_tsickle_Closure_declarations() {
/** @type {?} */
Table.decorators;
/**
 * @nocollapse
 * @type {?}
 */
Table.ctorParameters;
/** @type {?} */
Table.propDecorators;
/**
 * 样式前缀
 * @type {?}
 */
Table.prototype.prefixCls;
/**
 * 大小类型：正常型、紧凑型或迷你型，`default`, `middle`, `small`，默认为`default`
 * @type {?}
 */
Table.prototype.size;
/**
 * 表格标题
 * @type {?}
 */
Table.prototype.title;
/**
 * 表格脚注
 * @type {?}
 */
Table.prototype.footer;
/**
 * 固定列头
 * @type {?}
 */
Table.prototype.fixedHeader;
/**
 * 是否展示外边框和列边框
 * @type {?}
 */
Table.prototype.bordered;
/**
 * 是否显示表头
 * @type {?}
 */
Table.prototype.showHeader;
/**
 * 页面是否加载中
 * @type {?}
 */
Table.prototype.loading;
/**
 * 是否没有数据
 * @type {?}
 */
Table.prototype.isEmpty;
/**
 * 展示树形数据时，每层缩进的宽度，以 px 为单位
 * @type {?}
 */
Table.prototype.indentSize;
/**
 * 用户CSS样式
 * @type {?}
 */
Table.prototype.class;
/**
 * 国际化
 * @type {?}
 */
Table.prototype.locale;
/**
 * 固定表头或者固定列的设置，例如 { y: 240 } 表示内容部分的高度为240px，超出范围的内容需要滚动查看
 * @type {?}
 */
Table.prototype.scroll;
/** @type {?} */
Table.prototype.scrollPosition;
/**
 * 当固定列头时，可指定内容部分的最大高度
 * @type {?}
 */
Table.prototype.bodyMaxHeight;
/**
 * 内容部分的overflow样式
 * @type {?}
 */
Table.prototype.overflowY;
/**
 * 用户自定表格标题
 * @type {?}
 */
Table.prototype.tableHeader;
/**
 * 用户自定表格脚注
 * @type {?}
 */
Table.prototype.tableFooter;
/** @type {?} */
Table.prototype._renderer;
/** @type {?} */
Table.prototype._elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
